<script lang="ts">
	import { format } from 'svelte-i18n';
	import { tick } from 'svelte';
	import { profile_data } from 'src/stores/account/user';
	import { rooms } from 'src/stores/lists/rooms';
	import { dashboards } from 'src/stores/features/dashboards';
	import { main_menu_editor_open_store } from 'src/stores/ui/main-menu-editor';
	import IconSearchModal from 'src/components/Lists/IconSearchModal.svelte';
	import {
		ButtonV2,
		CardGrid,
		Icon,
		IconButton,
		ListNumberCard,
		Modal,
		Select,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import { onEndDrag, onStartDrag } from 'src/components/Lists/list-helpers';
	import { menuItemsNewLayout, MORE_MENU_ICON, type MenuItemDef } from 'src/helpers/main_menu';
	import {
		createCustomMenuItem,
		getDefaultMainMenuCustomization,
		MAIN_MENU_DEFAULT_IDS,
		MAIN_MENU_OPTIONAL_ITEMS,
		parseMainMenuCustomization,
		resolveCustomMenuItem,
		updateCustomMenuItem,
		validateMainMenuCustomization,
		type CustomMenuTargetType,
		type MainMenuCustomization,
	} from 'src/helpers/main_menu_customization';
	import { save_profile } from 'src/helpers/profile_helpers';

	type DraggableMenuItem = { id: string; isDndShadowItem?: boolean };

	let draft: MainMenuCustomization = parseMainMenuCustomization(null);
	let items: DraggableMenuItem[] = [];
	let dragDisabled = true;
	let saving = false;
	let editorInitialized = false;
	let editingCustomId: string | null = null;
	let showIconPicker = false;
	let customFormRef: HTMLElement;
	let targetType: CustomMenuTargetType | '' = '';
	let targetId = '';
	let selectedIcon = '';
	let validationError = '';

	$: if ($main_menu_editor_open_store && !editorInitialized) {
		// Opening the shared editor always starts from the last saved profile value.
		draft = parseMainMenuCustomization(
			$profile_data?.main_menu_customization,
			$profile_data?.menuOrder,
		);
		items = draft.order.map((id) => ({ id }));
		editorInitialized = true;
	}
	$: roomIds = Object.keys($rooms ?? {});
	$: dashboardIds = Object.keys($dashboards ?? {}).filter((id) => !['-2', '-3', '-4'].includes(id));
	$: targetOptions = (
		targetType === 'room' ? roomIds : targetType === 'dashboard' ? dashboardIds : []
	).map((id) => ({
		title: (targetType === 'room' ? $rooms[id] : $dashboards[id])?.name || id,
		value: id,
	}));
	$: typeOptions = [
		{ title: $format('label_main_menu_room', { default: 'Room' }), value: 'room' },
		{ title: $format('label_main_menu_dashboard', { default: 'Dashboard' }), value: 'dashboard' },
	];
	$: assistantMenuItem = menuItemsNewLayout.find((item) => item.id === 'assistant');
	$: builtInItems = [
		...MAIN_MENU_DEFAULT_IDS.map((id) => menuItemsNewLayout.find((item) => item.id === id)).filter(
			(item): item is MenuItemDef => !!item,
		),
		...MAIN_MENU_OPTIONAL_ITEMS,
	];
	$: itemDefinitions = new Map<string, MenuItemDef>([
		...menuItemsNewLayout.map((item) => [item.id ?? '', item] as const),
		...MAIN_MENU_OPTIONAL_ITEMS.map((item) => [item.id ?? '', item] as const),
		...draft.custom_items.map(
			(item) =>
				[
					item.id,
					resolveCustomMenuItem(item, $rooms ?? {}, $dashboards ?? {}) ?? {
						id: item.id,
						icon: item.icon,
						label: 'label_main_menu_unavailable',
						labelDefault: 'Unavailable shortcut',
					},
				] as const,
		),
	]);
	$: dndSettings = {
		items,
		flipDurationMs: 0,
		dragDisabled,
		autoAriaDisabled: true,
		type: 'mainMenuEditor',
		transformDraggedElement,
	};

	const transformDraggedElement = (draggedEl: HTMLElement) => {
		const child = draggedEl.firstElementChild as HTMLElement | null;
		child?.classList.add('on-drag');
	};

	const getSelectedValue = (option: string | { value: unknown }): string => {
		const value = typeof option === 'string' ? option : option.value;
		return typeof value === 'string' ? value : '';
	};

	const close = () => {
		main_menu_editor_open_store.set(false);
		items = [];
		editorInitialized = false;
		showIconPicker = false;
		clearCustomForm();
	};

	const clearCustomForm = () => {
		editingCustomId = null;
		validationError = '';
		targetType = '';
		targetId = '';
		selectedIcon = '';
	};

	const addItem = (id: string) => {
		if (items.some((item) => item.id === id)) return;
		items = [...items, { id }];
		validationError = '';
	};

	const hideItem = (id: string) => {
		items = items.filter((item) => item.id !== id);
		validationError = '';
	};

	const removeCustomItem = (id: string) => {
		hideItem(id);
		draft = { ...draft, custom_items: draft.custom_items.filter((item) => item.id !== id) };
		if (editingCustomId === id) clearCustomForm();
	};

	const startEditCustomItem = async (id: string) => {
		const item = draft.custom_items.find((candidate) => candidate.id === id);
		if (!item) return;
		editingCustomId = id;
		targetType = item.target_type;
		targetId = item.target_id;
		selectedIcon = item.icon;
		validationError = '';
		await tick();
		customFormRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	};

	const restoreDefaults = () => {
		draft = getDefaultMainMenuCustomization();
		items = draft.order.map((id) => ({ id }));
		clearCustomForm();
	};

	const addCustomItem = () => {
		const result = createCustomMenuItem(
			draft,
			targetType,
			targetId,
			selectedIcon,
			roomIds,
			dashboardIds,
		);
		if (result.error === 'invalid') {
			validationError = $format('alert_main_menu_invalid_shortcut', {
				default: 'Choose an existing destination and an icon.',
			});
			return;
		}
		if (result.error === 'duplicate') {
			validationError = $format('alert_main_menu_duplicate_shortcut', {
				default: 'This shortcut is already in the menu.',
			});
			return;
		}
		if (!result.item) return;
		draft = {
			...draft,
			custom_items: [...draft.custom_items, result.item],
		};
		addItem(result.item.id);
		clearCustomForm();
	};

	const saveCustomItem = () => {
		if (!editingCustomId) return;
		const result = updateCustomMenuItem(
			{ ...draft, order: items.map((item) => item.id) },
			editingCustomId,
			targetType,
			targetId,
			selectedIcon,
			roomIds,
			dashboardIds,
		);
		if (result.error === 'invalid') {
			validationError = $format('alert_main_menu_invalid_shortcut', {
				default: 'Choose an existing destination and an icon.',
			});
			return;
		}
		if (result.error === 'duplicate') {
			validationError = $format('alert_main_menu_duplicate_shortcut', {
				default: 'This shortcut is already in the menu.',
			});
			return;
		}
		if (!result.config) return;
		draft = result.config;
		items = result.config.order.map((id) => ({ id }));
		clearCustomForm();
	};

	const save = async () => {
		if (saving) return;
		const config: MainMenuCustomization = { ...draft, order: items.map((item) => item.id) };
		if (!validateMainMenuCustomization(config, roomIds, dashboardIds)) {
			validationError = $format('alert_main_menu_invalid_configuration', {
				default: 'Some shortcuts are no longer available. Please review the menu.',
			});
			return;
		}
		saving = true;
		try {
			const result = await save_profile('main_menu_customization', config);
			if (!result?.isok) throw new Error('Profile save failed');
			toast.open({
				type: 'success',
				title: $format('toast_main_menu_saved', { default: 'Main menu saved.' }),
			});
			close();
		} catch {
			validationError = $format('alert_main_menu_save_failed', {
				default: 'Could not save the main menu. Please try again.',
			});
		} finally {
			saving = false;
		}
	};
</script>

<Modal
	showModal={$main_menu_editor_open_store}
	title={$format('title_main_menu', { default: 'Main menu' })}
	on:close={close}
>
	<svelte:fragment slot="content">
		<Text class="mb-m" styleType="body-small">
			{$format('text_main_menu_editor_intro', {
				default: 'Drag to reorder shortcuts. Hide any shortcut you do not need.',
			})}
		</Text>
		<ButtonV2 class="mb-m" size="s" variant="secondary" on:click={restoreDefaults}>
			{$format('button_main_menu_restore_defaults', { default: 'Restore defaults' })}
		</ButtonV2>
		<CardGrid
			class="mb-m"
			sm={100}
			widthUnit="%"
			editMode
			modalContent
			noPaddingBottom
			{dndSettings}
			on:consider={(event) => (items = event.detail.items)}
			on:finalize={(event) => {
				items = [...event.detail.items];
				dragDisabled = true;
			}}
		>
			{#each items as item, index (item.id)}
				{@const definition = itemDefinitions.get(item.id)}
				{#if definition}
					<div class:dnd-placeholder={item.isDndShadowItem}>
						<ListNumberCard size="s" cardNumber={`${index + 1}`.padStart(2, '0')} editMode>
							<IconButton
								aria-label={$format('button_drag_to_reorder', {
									default: 'Drag to reorder',
								})}
								class="mr-s"
								icon="fas fa-up-down-left-right"
								size="s"
								variant="primary"
								dndHandle
								on:mousedown={(event) =>
									onStartDrag(event, true, () => (dragDisabled = false))}
								on:touchstart={(event) =>
									onStartDrag(event, true, () => (dragDisabled = false))}
								on:mouseup={(event) =>
									onEndDrag(event, true, () => (dragDisabled = true))}
								on:touchend={(event) =>
									onEndDrag(event, true, () => (dragDisabled = true))}
							/>
							<Icon class="mr-s" icon={definition.icon} size="s" />
							<Text styleType="controls-small" class="width-full">
								{$format(definition.label, { default: definition.labelDefault })}
							</Text>
							{#if item.id.startsWith('custom:')}
								<ButtonV2
									class="mr-s"
									size="s"
									variant="secondary"
									on:click={() => startEditCustomItem(item.id)}
								>
									{$format('button_edit', { default: 'Edit' })}
								</ButtonV2>
								<ButtonV2
									size="s"
									variant="secondary"
									on:click={() => removeCustomItem(item.id)}
								>
									{$format('button_remove', { default: 'Remove' })}
								</ButtonV2>
							{:else}
								<ButtonV2
									size="s"
									variant="secondary"
									on:click={() => hideItem(item.id)}
								>
									{$format('button_hide', { default: 'Hide' })}
								</ButtonV2>
							{/if}
						</ListNumberCard>
					</div>
				{/if}
			{/each}
		</CardGrid>
		<Text class="mb-s" styleType="h2">
			{$format('title_main_menu_fixed', { default: 'Fixed controls' })}
		</Text>
		{#if assistantMenuItem}
			<ListNumberCard size="s" icon="fa-solid fa-map-pin" editMode class="mb-s">
				<Icon class="mr-s" icon={assistantMenuItem.icon} size="s" />
				<Text styleType="controls-small" class="width-full">
					{$format(assistantMenuItem.label, { default: assistantMenuItem.labelDefault })}
				</Text>
			</ListNumberCard>
		{/if}
		<ListNumberCard size="s" icon="fa-solid fa-map-pin" editMode class="mb-m">
			<Icon class="mr-s" icon={MORE_MENU_ICON} size="s" />
			<Text styleType="controls-small" class="width-full">
				{$format('title_more', { default: 'More' })}
			</Text>
		</ListNumberCard>

		<Text class="mb-s" styleType="h2">
			{$format('title_main_menu_available', { default: 'Available shortcuts' })}
		</Text>
		{#each builtInItems.filter((item) => !items.some((selected) => selected.id === item.id)) as item (item.id)}
			<ListNumberCard size="s" class="mb-s" editMode>
				<Icon class="mr-s" icon={item.icon} size="s" />
				<Text styleType="controls-small" class="width-full">
					{$format(item.label, { default: item.labelDefault })}
				</Text>
				<ButtonV2 size="s" variant="secondary" on:click={() => addItem(item.id ?? '')}>
					{$format('button_add', { default: 'Add' })}
				</ButtonV2>
			</ListNumberCard>
		{/each}

		<div class="mt-m" bind:this={customFormRef}>
			<Text class="mb-s" styleType="h2">
				{#if editingCustomId}
					{$format('title_main_menu_edit_shortcut', { default: 'Edit shortcut' })}
				{:else}
					{$format('title_main_menu_custom', { default: 'Custom shortcut' })}
				{/if}
			</Text>
			<Select
				id="main-menu-target-type"
				aria-label={$format('label_main_menu_destination_type', { default: 'Destination type' })}
				label={$format('label_main_menu_destination_type', { default: 'Destination type' })}
				class="mb-s"
				icon={null}
				options={typeOptions}
				value={typeOptions.find((option) => option.value === targetType) ?? null}
				on:change={(event) => {
					const selectedType = getSelectedValue(event.detail);
					targetType =
						selectedType === 'room' || selectedType === 'dashboard' ? selectedType : '';
					targetId = '';
				}}
			/>
			<Select
				id="main-menu-target"
				aria-label={$format('label_main_menu_destination', { default: 'Destination' })}
				label={$format('label_main_menu_destination', { default: 'Destination' })}
				class="mb-s"
				icon={null}
				options={targetOptions}
				disabled={!targetType}
				value={targetOptions.find((option) => option.value === targetId) ?? null}
				on:change={(event) => (targetId = getSelectedValue(event.detail))}
			/>
			<Text class="mb-s" styleType="body-small">
				{$format('label_main_menu_icon', { default: 'Shortcut icon' })}
			</Text>
			<ButtonV2 class="mb-s" size="m" variant="secondary" on:click={() => (showIconPicker = true)}>
				{#if selectedIcon}<Icon class="mr-s" icon={selectedIcon} size="s" />{/if}
				{$format('button_main_menu_choose_icon', { default: 'Choose icon' })}
			</ButtonV2>
			<div>
				{#if editingCustomId}
					<ButtonV2 class="mr-s" size="m" variant="secondary" on:click={clearCustomForm}>
						{$format('button_cancel', { default: 'Cancel' })}
					</ButtonV2>
					<ButtonV2 size="m" variant="secondary" on:click={saveCustomItem}>
						{$format('button_main_menu_update_shortcut', { default: 'Update shortcut' })}
					</ButtonV2>
				{:else}
					<ButtonV2 size="m" variant="secondary" on:click={addCustomItem}>
						{$format('button_main_menu_add_shortcut', { default: 'Add shortcut' })}
					</ButtonV2>
				{/if}
			</div>
		</div>
		{#if validationError}
			<Text class="mt-s" role="alert">{validationError}</Text>
		{/if}
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2 class="mr-s" size="m" variant="secondary" on:click={close}>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2 size="m" disabled={saving || !!editingCustomId} on:click={save}>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>

<IconSearchModal
	showModal={showIconPicker}
	on:close={() => (showIconPicker = false)}
	on:icon-selected={(event) => {
		selectedIcon = event.detail.icon;
		showIconPicker = false;
	}}
/>
